/**
 * web/src/lib/queryKeys.ts
 * Single source of truth for every TanStack Query cache key. Replaces ~30
 * stringly-typed literal arrays scattered across query hooks, mutation hooks,
 * and the WS dispatcher whose prefix relationships were enforced only by
 * hand-written comments (the Confirmations reconcile bug, F12, was the direct
 * cost of one such key drifting silently).
 *
 * Hierarchical TanStack v5 pattern: a `root()` returns the broad prefix used
 * by mutations/WS for invalidation + `setQueriesData`, and the parameterized
 * builders return the exact key a `useQuery` subscribes to. React Query
 * matches by left-prefix, so `invalidateQueries({ queryKey: qk.workflows.root() })`
 * reaches every `qk.workflows.list(...)` entry.
 *
 * The returned tuples are `as const` so the key shapes are statically visible
 * and a typo can't silently create a never-matched key.
 */

export const qk = {
  /** Current authenticated user (`/api/me`). */
  me: () => ['me'] as const,

  events: {
    /** Event list (`/api/events`). */
    all: () => ['events'] as const,
    /** Single event by id (id may be undefined while the query is disabled). */
    detail: (id: string | undefined) => ['event', id] as const,
    /** Per-event integration health (id may be undefined while disabled). */
    health: (id: string | undefined) => ['event-health', id] as const,
    /** Stored ops next-steps template sources (id may be undefined while disabled). */
    templates: (id: string | undefined) => ['event-templates', id] as const,
  },

  brands: {
    /** Brand list (`/api/brands`). */
    all: () => ['brands'] as const,
    /** Single brand by id (id may be undefined while the query is disabled). */
    detail: (id: string | undefined) => ['brand', id] as const,
  },

  /** Google Drive/Sheet resource metadata for an event config field. */
  googleResource: (kind: string, id: string | undefined, brandId?: string) =>
    ['google-resource', kind, id, brandId ?? null] as const,

  workflows: {
    /** Broad prefix — list patch/invalidate target (matches every list() key). */
    root: () => ['workflows'] as const,
    /** Filtered workflow list for the WorkflowList page. */
    list: (q: {
      eventId?: string | null;
      type?: string | null;
      status?: string | null;
      showArchived?: boolean;
    }) =>
      [
        'workflows',
        q.eventId ?? null,
        q.type ?? null,
        q.status ?? null,
        q.showArchived ?? false,
      ] as const,
  },

  workflow: {
    /** Broad prefix for one workflow (matches every detail() page/cursor). */
    root: (id: string) => ['workflow', id] as const,
    /** Broad prefix across EVERY workflow detail, id-less. Only for bulk
     * teardown (the admin purge) where no single id is the target — a
     * per-workflow mutation should use `root(id)` so it can't clobber
     * unrelated detail caches. */
    allRoot: () => ['workflow'] as const,
    /** Workflow detail with audit-log pagination cursor + limit (id may be
     * undefined while the query is disabled). */
    detail: (id: string | undefined, cursor: string | null | undefined, limit: number) =>
      ['workflow', id, cursor ?? null, limit] as const,
    /** Ops-gate email thread, scoped to the selected send-from connection. */
    opsThread: (id: string | undefined, sendFromConnectionId: string | null | undefined) =>
      ['workflow', id, 'ops-thread', sendFromConnectionId ?? null] as const,
  },

  gates: {
    /** Broad prefix — invalidate target matching every event-scoped variant. */
    root: () => ['gates', 'pending'] as const,
    /** Pending + recently-rejected gates for the approval queue, scoped to one
     * event (null = all accessible). Mirrors qk.workflows.list eventId scoping
     * so the Approvals tab honors the active-event selection. */
    pending: (eventId?: string | null) => ['gates', 'pending', eventId ?? null] as const,
  },

  speakers: {
    /** Broad prefix — invalidate target across roster + every journey. */
    root: () => ['speakers'] as const,
    /** Speaker roster scoped by event (or undefined for all accessible). */
    list: (eventId: string | null | undefined) => ['speakers', eventId ?? null] as const,
    /** One speaker's journey (hub) — keyed by event + monday item id. */
    journey: (eventId: string, itemId: string) => ['speakers', 'journey', eventId, itemId] as const,
    /** Read-only preview of a date-armed gate's email (logistics/thank_you). */
    emailPreview: (eventId: string, itemId: string, stepId: string) =>
      ['speakers', 'email-preview', eventId, itemId, stepId] as const,
    /** Pre-start past-speaker lookup — keyed by board item OR typed email. */
    previousCheck: (eventId: string, target: string) =>
      ['speakers', 'previous-check', eventId, target] as const,
    /** Kickoff thread-picker — recent Gmail threads with a speaker email. */
    inviteThreads: (eventId: string, email: string) =>
      ['speakers', 'invite-threads', eventId, email] as const,
    /** Speaker Cards pipeline board — NESTED under the speakers root so the
     * WS dispatcher's `qk.speakers.root()` invalidations refresh it for free. */
    cardsBoard: (eventId: string | null | undefined) =>
      ['speakers', 'cards-board', eventId ?? null] as const,
  },

  accommodations: {
    /** Broad prefix — invalidate target across every event's report. */
    root: () => ['accommodations'] as const,
    /** Pickup report for one event (id may be undefined while disabled). */
    report: (eventId: string | null | undefined) => ['accommodations', eventId ?? null] as const,
    /** Booking-pace curve for one event, scoped by hotel (null = all hotels). */
    pace: (eventId: string | null | undefined, hotelId: string | null | undefined) =>
      ['accommodations', eventId ?? null, 'pace', hotelId ?? null] as const,
  },

  sponsorRoi: {
    /** Broad prefix — invalidate every sponsor-ROI query for an event. */
    root: (eventId: string | null | undefined) => ['sponsor-roi', eventId ?? null] as const,
    /** Everyday screen: latest export + enriched preflight + setup. */
    preview: (eventId: string | null | undefined) =>
      ['sponsor-roi', eventId ?? null, 'preview'] as const,
    /** Raw setup config (Setup drawer). */
    config: (eventId: string | null | undefined) =>
      ['sponsor-roi', eventId ?? null, 'config'] as const,
    /** Runs list for one event (active-only when `active`). */
    runs: (eventId: string | null | undefined, active: boolean) =>
      ['sponsor-roi', eventId ?? null, 'runs', active] as const,
    /** One run's detail + items (polled while running). */
    run: (eventId: string | null | undefined, runId: string) =>
      ['sponsor-roi', eventId ?? null, 'run', runId] as const,
  },

  stats: {
    /** Broad prefix — invalidate target for any stat change. */
    root: () => ['stats'] as const,
    /** Stats banner scoped to one event (or undefined for all). */
    forEvent: (eventId: string | null | undefined) => ['stats', eventId] as const,
  },

  trackedEmails: {
    /** Broad prefix — patch/invalidate target across all status filters (F12). */
    root: () => ['tracked-emails'] as const,
    /** Tracked-email list scoped by event + status + workflow. */
    list: (q: { eventId?: string | null; status?: string | null; workflowId?: string | null }) =>
      ['tracked-emails', q.eventId ?? null, q.status ?? null, q.workflowId ?? null] as const,
  },

  /** Admin access matrix (users × roles/permissions). */
  adminAccess: () => ['admin-access'] as const,

  /** Per-workflow live activity ring buffer (WS step_activity feed). */
  activity: (workflowId: string) => ['activity', workflowId] as const,

  /** Broad prefix across every workflow's activity buffer. Bulk-teardown only
   * (see `workflow.allRoot`). */
  activityRoot: () => ['activity'] as const,
} as const;
